import React, { useState, useEffect, useRef } from 'react';
import { motion, useScroll, useTransform, AnimatePresence, useInView, useReducedMotion } from 'framer-motion';
import { GoogleLogin } from '@react-oauth/google';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useTranslation } from '@/contexts/I18nContext';
import { authAPI, giftAPI } from '@/lib/api';
import { LanguageSwitcher } from '@/components/LanguageSwitcher';
import {
  BookOpen, Heart, Users, Mic, Clock, Shield, MessageSquare, ChevronRight,
  Mail, Lock, User, Gift, Zap, Star, Crown, Building, Check, Calendar,
  ArrowRight, Sparkles, Play, Volume2
} from 'lucide-react';
import { toast } from 'sonner';
import SEO from '@/components/SEO';
import seoLanguages from '@/seo/languages.json';
import BuiltBy from '@/components/BuiltBy';
import seoPages from '@/seo/pages.json';

const GUIDES = Object.entries(seoPages.pages).filter(([, p]) => p.group === 'questions-guide');

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH

/* ────────────────────────────────────────────────────────────────
   Rotating word component — cycles through related concepts
   ──────────────────────────────────────────────────────────────── */
function RotatingWord({ words, interval = 2400 }) {
  const [index, setIndex] = useState(0);
  const reduce = useReducedMotion();

  useEffect(() => {
    if (reduce) return;
    const id = setInterval(() => setIndex((i) => (i + 1) % words.length), interval);
    return () => clearInterval(id);
  }, [words.length, interval, reduce]);

  return (
    <span className="inline-block relative" style={{ minWidth: '6ch' }}>
      <AnimatePresence mode="wait">
        <motion.span
          key={words[index]}
          initial={reduce ? {} : { opacity: 0, y: 20, filter: 'blur(8px)' }}
          animate={reduce ? {} : { opacity: 1, y: 0, filter: 'blur(0px)' }}
          exit={reduce ? {} : { opacity: 0, y: -20, filter: 'blur(8px)' }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          className="inline-block italic"
          style={{ color: '#b45309' }}
        >
          {words[index]}
        </motion.span>
      </AnimatePresence>
    </span>
  );
}

/* ────────────────────────────────────────────────────────────────
   Typewriter — reveals text character by character
   ──────────────────────────────────────────────────────────────── */
function Typewriter({ text, speed = 30, startDelay = 0, onDone }) {
  const [shown, setShown] = useState('');
  const reduce = useReducedMotion();

  useEffect(() => {
    if (reduce) {
      setShown(text);
      onDone?.();
      return;
    }
    setShown('');
    let i = 0;
    const startId = setTimeout(() => {
      const id = setInterval(() => {
        i++;
        setShown(text.slice(0, i));
        if (i >= text.length) {
          clearInterval(id);
          onDone?.();
        }
      }, speed);
    }, startDelay);
    return () => clearTimeout(startId);
  }, [text, speed, startDelay, reduce, onDone]);

  return <span>{shown}</span>;
}

/* ────────────────────────────────────────────────────────────────
   Animated mock conversation — plays when scrolled into view
   ──────────────────────────────────────────────────────────────── */
function ConversationDemo({ t }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-20%' });
  const [step, setStep] = useState(0);
  const reduce = useReducedMotion();

  const messages = [
    { role: 'ai', text: t('v2.chat1') || "Tell me about the house where you grew up.", delay: 300 },
    { role: 'user', text: t('v2.chat2') || "We lived in a little blue house by the river. My father built the porch with his own hands.", delay: 1200 },
    { role: 'ai', text: t('v2.chat3') || "That sounds lovely. What's the first thing you remember about that porch?", delay: 1600 },
    { role: 'user', text: t('v2.chat4') || "My grandmother shelling peas on summer evenings. I can still hear her humming.", delay: 1200 },
  ];

  useEffect(() => {
    if (!inView) return;
    if (reduce) { setStep(messages.length); return; }
    let cumulative = 0;
    const timers = messages.map((m, i) => {
      cumulative += m.delay;
      return setTimeout(() => setStep(i + 1), cumulative);
    });
    return () => timers.forEach(clearTimeout);
  }, [inView, reduce]);

  return (
    <div ref={ref} className="space-y-3 min-h-[320px]">
      {messages.slice(0, step).map((m, i) => (
        <motion.div
          key={i}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}
        >
          <div
            className={`max-w-[85%] px-5 py-3 text-base leading-relaxed ${
              m.role === 'user'
                ? 'bg-[#1c1917] text-[#fafaf9] rounded-[20px] rounded-br-[8px]'
                : 'bg-white border border-[#d6d3d1]/60 text-[#1c1917] rounded-[20px] rounded-bl-[8px] shadow-sm'
            }`}
          >
            {m.text}
          </div>
        </motion.div>
      ))}
      {/* Typing indicator */}
      {step < messages.length && step > 0 && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className={`flex ${messages[step].role === 'user' ? 'justify-end' : 'justify-start'}`}
        >
          <div className={`px-5 py-3 rounded-[20px] ${
            messages[step].role === 'user' ? 'bg-[#1c1917]/30' : 'bg-white border border-[#d6d3d1]/60 shadow-sm'
          }`}>
            <div className="flex gap-1.5">
              <span className="w-2 h-2 rounded-full bg-[#b45309] animate-pulse" style={{ animationDelay: '0ms' }} />
              <span className="w-2 h-2 rounded-full bg-[#b45309] animate-pulse" style={{ animationDelay: '200ms' }} />
              <span className="w-2 h-2 rounded-full bg-[#b45309] animate-pulse" style={{ animationDelay: '400ms' }} />
            </div>
          </div>
        </motion.div>
      )}
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────
   Horizontal timeline — decades unfurl as you scroll
   ──────────────────────────────────────────────────────────────── */
function LivingTimeline({ t }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start end', 'end start'] });
  const x = useTransform(scrollYProgress, [0, 1], ['0%', '-35%']);
  const reduce = useReducedMotion();

  const decades = [
    { year: '1950s', label: t('v2.decade1') || 'Born in a quiet village', emoji: '👶' },
    { year: '1960s', label: t('v2.decade2') || 'Childhood summers', emoji: '🌾' },
    { year: '1970s', label: t('v2.decade3') || 'First job, first love', emoji: '💼' },
    { year: '1980s', label: t('v2.decade4') || 'Building a family', emoji: '👨‍👩‍👧' },
    { year: '1990s', label: t('v2.decade5') || 'Children grow up', emoji: '🎓' },
    { year: '2000s', label: t('v2.decade6') || 'Grandchildren arrive', emoji: '👶' },
    { year: '2010s', label: t('v2.decade7') || 'Retirement journeys', emoji: '✈️' },
    { year: '2020s', label: t('v2.decade8') || 'Sharing wisdom', emoji: '📖' },
  ];

  return (
    <div ref={ref} className="relative overflow-hidden py-8">
      <motion.div
        style={reduce ? {} : { x }}
        className="flex gap-6 pl-6"
      >
        {decades.map((d, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: i * 0.08 }}
            className="flex-shrink-0 w-64 bg-white rounded-3xl border border-[#d6d3d1]/50 p-6 shadow-[0_4px_20px_-4px_rgba(28,25,23,0.06)]"
          >
            <div className="text-5xl mb-3">{d.emoji}</div>
            <p className="font-['Playfair_Display'] text-2xl font-medium text-[#b45309]">{d.year}</p>
            <p className="text-[#44403c] mt-2 leading-relaxed">{d.label}</p>
          </motion.div>
        ))}
      </motion.div>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────
   Main component
   ──────────────────────────────────────────────────────────────── */
export default function LandingPageV2({ lang }) {
  const { login } = useAuth();
  const { t, language, setLanguage } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [authMode, setAuthMode] = useState('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [searchParams] = useSearchParams();
  const [showAuth, setShowAuth] = useState(false);
  const authRef = useRef(null);
  const reduce = useReducedMotion();

  // Hero background parallax
  const { scrollY } = useScroll();
  const bgY = useTransform(scrollY, [0, 800], ['0%', '30%']);

  useEffect(() => {
    const ref = searchParams.get('ref');
    if (ref) sessionStorage.setItem('legacylens_ref', ref);
  }, [searchParams]);

  useEffect(() => {
    const verify = searchParams.get('verify');
    if (verify === 'success') toast.success(t('landing.emailVerified') || 'Email verified successfully!');
    else if (verify === 'expired') toast.error(t('landing.verifyExpired') || 'Verification link expired. Please register again.');
    else if (verify === 'invalid') toast.error(t('landing.verifyInvalid') || 'Invalid verification link.');
  }, [searchParams, t]);

  const autoClaimGift = async () => {
    const giftCode = sessionStorage.getItem('legacylens_gift_code');
    if (!giftCode) return;
    try {
      await giftAPI.claim(giftCode);
      sessionStorage.removeItem('legacylens_gift_code');
      toast.success(t('gift.claimed') || 'Gift credit added to your account!');
    } catch {
      sessionStorage.removeItem('legacylens_gift_code');
    }
  };

  const handleGoogleSuccess = async (credentialResponse) => {
    setIsLoading(true);
    try {
      const ref = sessionStorage.getItem('legacylens_ref');
      const res = await authAPI.googleAuth(credentialResponse.credential, ref);
      await login(res.data.token, res.data.user);
      await autoClaimGift();
      toast.success(t('landing.welcome'));
    } catch (err) {
      toast.error(err.response?.data?.detail || t('landing.googleError'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleEmailAuth = async (e) => {
    e.preventDefault();
    if (authMode === 'forgot') {
      setIsLoading(true);
      try {
        await authAPI.forgotPassword(email, language);
        toast.success(t('landing.resetSent') || 'If this email is registered, a reset link has been sent');
        setAuthMode('login');
      } catch { toast.error(t('landing.authError') || 'Something went wrong'); }
      finally { setIsLoading(false); }
      return;
    }
    if (password.length < 8) {
      toast.error(t('landing.passwordTooShort') || 'Password must be at least 8 characters');
      return;
    }
    setIsLoading(true);
    try {
      let res;
      if (authMode === 'register') {
        if (!name.trim()) { toast.error(t('landing.nameRequired') || 'Name is required'); setIsLoading(false); return; }
        const ref = sessionStorage.getItem('legacylens_ref');
        res = await authAPI.register({ email, password, name: name.trim(), language, ref });
        toast.success(t('landing.verifyEmailSent') || 'Check your email to verify your account');
      } else {
        res = await authAPI.login({ email, password });
      }
      await login(res.data.token, res.data.user);
      await autoClaimGift();
      if (authMode === 'login') toast.success(t('landing.welcome'));
    } catch (err) {
      toast.error(err.response?.data?.detail || t('landing.authError') || 'Authentication failed');
    } finally {
      setIsLoading(false);
    }
  };

  const scrollToAuth = () => {
    setShowAuth(true);
    setTimeout(() => {
      authRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 100);
  };

  const rotatingWords = [
    t('v2.word1') || 'story',
    t('v2.word2') || 'voice',
    t('v2.word3') || 'memory',
    t('v2.word4') || 'legacy',
    t('v2.word5') || 'life',
  ];

  return (
    <div className="min-h-screen bg-[#fafaf9] relative overflow-x-hidden">
      {lang ? <SEO landing={lang} /> : <SEO path="/" />}

      {/* Ambient warm gradients that drift */}
      <motion.div
        style={reduce ? {} : { y: bgY }}
        className="fixed inset-0 overflow-hidden pointer-events-none"
      >
        <div className="absolute top-[-10%] right-[-10%] w-[700px] h-[700px] bg-[#b45309]/[0.08] rounded-full blur-[140px]" />
        <div className="absolute top-[30%] left-[-15%] w-[500px] h-[500px] bg-[#92400e]/[0.06] rounded-full blur-[120px]" />
        <div className="absolute bottom-[0%] right-[20%] w-[600px] h-[600px] bg-[#fed7aa]/[0.15] rounded-full blur-[140px]" />
      </motion.div>

      <div className="relative z-10">
        {/* ═══════════════════ HEADER ═══════════════════ */}
        <motion.header
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="sticky top-0 z-50 backdrop-blur-md bg-[#fafaf9]/70 border-b border-[#d6d3d1]/30"
        >
          <div className="max-w-7xl mx-auto px-6 md:px-8 flex items-center justify-between py-4">
            <div className="flex items-center gap-3">
              <motion.div
                whileHover={reduce ? {} : { rotate: [0, -5, 5, 0] }}
                transition={{ duration: 0.5 }}
                className="w-10 h-10 rounded-full bg-gradient-to-br from-[#b45309] to-[#92400e] flex items-center justify-center shadow-md"
              >
                <BookOpen className="w-5 h-5 text-white" />
              </motion.div>
              <span className="font-['Playfair_Display'] text-2xl font-medium text-[#1c1917]">
                LegacyLens
              </span>
            </div>
            <div className="flex items-center gap-3 md:gap-4">
              <LanguageSwitcher />
              <a href="/replica" className="hidden sm:inline-block text-[#78716c] hover:text-[#b45309] transition-colors text-base font-medium">
                {t('nav.descendantAccess')}
              </a>
              <button
                onClick={scrollToAuth}
                className="bg-[#1c1917] hover:bg-[#292524] text-white text-sm font-medium px-5 py-2 rounded-full transition-colors"
              >
                {t('v2.signIn') || 'Sign in'}
              </button>
            </div>
          </div>
        </motion.header>

        {/* ═══════════════════ HERO ═══════════════════ */}
        <section className="relative">
          <div className="max-w-7xl mx-auto px-6 md:px-8 pt-16 md:pt-24 pb-16">
            <div className="grid lg:grid-cols-12 gap-12 items-center">
              {/* Left: headline */}
              <div className="lg:col-span-7">
                <motion.p
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6 }}
                  className="inline-flex items-center gap-2 text-sm font-medium text-[#b45309] mb-6 bg-[#b45309]/10 px-4 py-1.5 rounded-full"
                >
                  <Sparkles className="w-4 h-4" />
                  {t('v2.badge') || 'Powered by AI — made with heart'}
                </motion.p>

                <motion.h1
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.1 }}
                  className="font-['Playfair_Display'] text-[2.75rem] sm:text-5xl md:text-6xl lg:text-7xl font-medium text-[#1c1917] leading-[1.05] tracking-tight mb-8"
                >
                  {t('v2.heroBefore') || 'Every'}{' '}
                  <RotatingWord words={rotatingWords} />{' '}
                  <br className="hidden sm:block" />
                  {t('v2.heroAfter') || 'deserves to be heard.'}
                </motion.h1>

                <motion.p
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.3 }}
                  className="text-lg md:text-xl text-[#44403c] leading-relaxed mb-10 max-w-xl"
                >
                  {t('v2.heroSubtitle') || 'LegacyLens listens with patience, remembers with care, and keeps your family\'s voice alive — for the generations still to come.'}
                </motion.p>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.5 }}
                  className="flex flex-col sm:flex-row gap-4"
                >
                  <button
                    onClick={scrollToAuth}
                    className="group inline-flex items-center justify-center gap-2 bg-[#b45309] hover:bg-[#92400e] text-white text-base md:text-lg font-medium px-8 py-4 rounded-full shadow-[0_8px_24px_-8px_rgba(180,83,9,0.5)] hover:shadow-[0_12px_32px_-8px_rgba(180,83,9,0.6)] transition-all"
                  >
                    {t('v2.ctaPrimary') || 'Begin your story'}
                    <motion.span
                      animate={reduce ? {} : { x: [0, 4, 0] }}
                      transition={{ duration: 1.6, repeat: Infinity, ease: 'easeInOut' }}
                    >
                      <ArrowRight className="w-5 h-5" />
                    </motion.span>
                  </button>
                  <a
                    href="#how"
                    className="inline-flex items-center justify-center gap-2 bg-white hover:bg-[#f5f5f4] text-[#1c1917] text-base md:text-lg font-medium px-8 py-4 rounded-full border border-[#d6d3d1] transition-colors"
                  >
                    {t('v2.ctaSecondary') || 'See how it works'}
                  </a>
                </motion.div>

                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.8, delay: 0.8 }}
                  className="text-sm text-[#78716c] mt-6"
                >
                  {t('v2.heroNote') || '3 free conversations · no credit card · private by design'}
                </motion.p>
              </div>

              {/* Right: live chat demo */}
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, delay: 0.4 }}
                className="lg:col-span-5"
              >
                <div className="relative">
                  {/* Floating orbit dots (gentle) */}
                  {!reduce && (
                    <>
                      <motion.div
                        animate={{ y: [0, -12, 0] }}
                        transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
                        className="absolute -top-4 -right-2 w-3 h-3 rounded-full bg-[#b45309]/40"
                      />
                      <motion.div
                        animate={{ y: [0, 10, 0] }}
                        transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut', delay: 1 }}
                        className="absolute -bottom-2 -left-4 w-4 h-4 rounded-full bg-[#fed7aa]"
                      />
                    </>
                  )}
                  <div className="glass-card rounded-[28px] p-6 md:p-8 shadow-[0_20px_60px_-20px_rgba(28,25,23,0.2)]">
                    <div className="flex items-center gap-2 mb-5 pb-4 border-b border-[#d6d3d1]/30">
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#b45309] to-[#92400e] flex items-center justify-center">
                        <BookOpen className="w-4 h-4 text-white" />
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-medium text-[#1c1917]">{t('v2.chatTitle') || 'LegacyLens Biographer'}</p>
                        <p className="text-xs text-[#78716c]">{t('v2.chatSubtitle') || 'Listening with care…'}</p>
                      </div>
                      <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                    </div>
                    <ConversationDemo t={t} />
                  </div>
                </div>
              </motion.div>
            </div>
          </div>

          {/* Soft wave divider */}
          <div className="relative h-16 overflow-hidden">
            <svg
              className="absolute bottom-0 w-full h-full"
              viewBox="0 0 1440 100"
              preserveAspectRatio="none"
              aria-hidden="true"
            >
              <path
                d="M0,60 C240,100 480,20 720,40 C960,60 1200,100 1440,70 L1440,100 L0,100 Z"
                fill="#ffffff"
                opacity="0.6"
              />
            </svg>
          </div>
        </section>

        {/* ═══════════════════ HOW IT WORKS ═══════════════════ */}
        <section id="how" className="py-20 md:py-32 bg-white/60">
          <div className="max-w-6xl mx-auto px-6 md:px-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-80px' }}
              transition={{ duration: 0.7 }}
              className="text-center mb-16"
            >
              <p className="text-sm uppercase tracking-[0.2em] font-semibold text-[#b45309] mb-4">
                {t('v2.howLabel') || 'How it works'}
              </p>
              <h2 className="font-['Playfair_Display'] text-4xl md:text-5xl font-medium text-[#1c1917] mb-5 leading-tight">
                {t('v2.howTitle') || 'A conversation, not a questionnaire.'}
              </h2>
              <p className="text-lg text-[#57534e] max-w-2xl mx-auto leading-relaxed">
                {t('v2.howDesc') || 'Your AI biographer asks one gentle question at a time. You speak. It remembers everything. Over weeks, a complete life story takes shape.'}
              </p>
            </motion.div>

            <div className="grid md:grid-cols-3 gap-8 md:gap-10">
              {[
                {
                  icon: Mic,
                  num: '01',
                  title: t('v2.step1Title') || 'Speak naturally',
                  desc: t('v2.step1Desc') || 'No typing required. Just talk, in any of 15 languages.',
                },
                {
                  icon: Heart,
                  num: '02',
                  title: t('v2.step2Title') || 'Gently guided',
                  desc: t('v2.step2Desc') || 'The biographer asks one question at a time — and truly listens.',
                },
                {
                  icon: BookOpen,
                  num: '03',
                  title: t('v2.step3Title') || 'Beautifully preserved',
                  desc: t('v2.step3Desc') || 'Your stories become a searchable timeline, a PDF book, and a voice your family can talk to.',
                },
              ].map((step, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-60px' }}
                  transition={{ duration: 0.6, delay: i * 0.12 }}
                  className="group relative"
                >
                  <div className="bg-white rounded-3xl border border-[#d6d3d1]/50 p-8 md:p-10 h-full transition-all duration-500 hover:shadow-[0_20px_50px_-20px_rgba(180,83,9,0.3)] hover:border-[#b45309]/30 hover:-translate-y-1">
                    <div className="flex items-center gap-4 mb-6">
                      <span className="font-['Playfair_Display'] text-5xl font-medium text-[#b45309]/20 transition-colors group-hover:text-[#b45309]/40">
                        {step.num}
                      </span>
                      <div className="w-12 h-12 rounded-2xl bg-[#b45309]/10 flex items-center justify-center">
                        <step.icon className="w-6 h-6 text-[#b45309]" />
                      </div>
                    </div>
                    <h3 className="font-['Playfair_Display'] text-2xl font-medium text-[#1c1917] mb-3">{step.title}</h3>
                    <p className="text-[#57534e] leading-relaxed text-base">{step.desc}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* ═══════════════════ LIVING TIMELINE ═══════════════════ */}
        <section className="py-20 md:py-28 overflow-hidden">
          <div className="max-w-6xl mx-auto px-6 md:px-8 mb-10">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
              className="max-w-2xl"
            >
              <p className="text-sm uppercase tracking-[0.2em] font-semibold text-[#b45309] mb-4">
                {t('v2.timelineLabel') || 'A living timeline'}
              </p>
              <h2 className="font-['Playfair_Display'] text-4xl md:text-5xl font-medium text-[#1c1917] mb-5 leading-tight">
                {t('v2.timelineTitle') || 'Every decade, a chapter.'}
              </h2>
              <p className="text-lg text-[#57534e] leading-relaxed">
                {t('v2.timelineDesc') || 'As you share, the biographer quietly builds a visual timeline of your life — every event placed in its decade, every person remembered, every location mapped.'}
              </p>
            </motion.div>
          </div>
          <LivingTimeline t={t} />
        </section>

        {/* ═══════════════════ WHY / VALUES ═══════════════════ */}
        <section className="py-20 md:py-28 bg-gradient-to-b from-transparent via-white/40 to-transparent">
          <div className="max-w-6xl mx-auto px-6 md:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
              className="text-center mb-16"
            >
              <p className="text-sm uppercase tracking-[0.2em] font-semibold text-[#b45309] mb-4">
                {t('v2.whyLabel') || 'Why LegacyLens'}
              </p>
              <h2 className="font-['Playfair_Display'] text-4xl md:text-5xl font-medium text-[#1c1917] leading-tight max-w-3xl mx-auto">
                {t('v2.whyTitle') || 'Not another app. A quiet act of love.'}
              </h2>
            </motion.div>

            <div className="grid md:grid-cols-3 gap-6 md:gap-8">
              {[
                {
                  icon: Volume2,
                  title: t('v2.value1Title') || 'Listens',
                  desc: t('v2.value1Desc') || 'Voice-first. Patient. Never interrupts. Works in 15 languages and understands the rhythms of how elders speak.',
                  accent: 'from-amber-100 to-orange-50',
                },
                {
                  icon: Clock,
                  title: t('v2.value2Title') || 'Remembers',
                  desc: t('v2.value2Desc') || 'Every person, place and date is woven into a timeline that grows with each conversation. Nothing is lost.',
                  accent: 'from-rose-100 to-amber-50',
                },
                {
                  icon: Shield,
                  title: t('v2.value3Title') || 'Protects',
                  desc: t('v2.value3Desc') || 'Private by design. Encrypted. Your stories are yours — to share with whom you choose, when you choose.',
                  accent: 'from-stone-100 to-neutral-50',
                },
              ].map((v, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-60px' }}
                  transition={{ duration: 0.6, delay: i * 0.12 }}
                  className={`relative rounded-3xl p-8 md:p-10 bg-gradient-to-br ${v.accent} border border-white/80`}
                >
                  <div className="w-14 h-14 rounded-2xl bg-white/80 backdrop-blur flex items-center justify-center mb-6 shadow-sm">
                    <v.icon className="w-7 h-7 text-[#b45309]" />
                  </div>
                  <h3 className="font-['Playfair_Display'] text-3xl font-medium text-[#1c1917] mb-3">{v.title}</h3>
                  <p className="text-[#44403c] leading-relaxed text-base">{v.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* ═══════════════════ TESTIMONIALS AS STORIES ═══════════════════ */}
        <section className="py-20 md:py-32">
          <div className="max-w-5xl mx-auto px-6 md:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
              className="text-center mb-16"
            >
              <p className="text-sm uppercase tracking-[0.2em] font-semibold text-[#b45309] mb-4">
                {t('v2.storiesLabel') || 'Real families'}
              </p>
              <h2 className="font-['Playfair_Display'] text-4xl md:text-5xl font-medium text-[#1c1917] leading-tight">
                {t('v2.storiesTitle') || 'Stories that now live on.'}
              </h2>
            </motion.div>

            <div className="grid md:grid-cols-3 gap-6">
              {[
                {
                  quote: t('landing.testimonial1') || "I had always meant to sit down with Dad and write his stories. Now we do it together, just by talking. I've already learned things about his childhood I never knew.",
                  name: 'Margaret K.',
                  role: t('landing.testimonial1Role') || 'Daughter · Portland, OR',
                },
                {
                  quote: t('landing.testimonial2') || 'My grandmother speaks Hungarian. My kids speak English. LegacyLens lets them finally have a real conversation across the language barrier.',
                  name: 'Daniel V.',
                  role: t('landing.testimonial2Role') || 'Grandson · New York',
                },
                {
                  quote: t('landing.testimonial3') || "At 87, I'd never have thought I'd enjoy talking to a computer. But it listens better than most people do. I look forward to our sessions.",
                  name: 'Anna M.',
                  role: t('landing.testimonial3Role') || 'Storyteller · 87',
                },
              ].map((s, i) => (
                <motion.figure
                  key={i}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-60px' }}
                  transition={{ duration: 0.6, delay: i * 0.12 }}
                  className="bg-white rounded-3xl border border-[#d6d3d1]/50 p-8 relative"
                >
                  <span className="absolute top-4 left-6 font-['Playfair_Display'] text-6xl leading-none text-[#b45309]/20 select-none">
                    &ldquo;
                  </span>
                  <blockquote className="text-[#1c1917] leading-relaxed text-base italic pt-8">
                    {s.quote}
                  </blockquote>
                  <figcaption className="mt-6 pt-5 border-t border-[#f5f5f4]">
                    <p className="font-medium text-[#1c1917]">— {s.name}</p>
                    <p className="text-sm text-[#78716c] mt-0.5">{s.role}</p>
                  </figcaption>
                </motion.figure>
              ))}
            </div>
          </div>
        </section>

        {/* ═══════════════════ PRICING ═══════════════════ */}
        <section className="py-20 md:py-28 bg-white/60">
          <div className="max-w-5xl mx-auto px-6 md:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
              className="text-center mb-14"
            >
              <p className="text-sm uppercase tracking-[0.2em] font-semibold text-[#b45309] mb-4">
                {t('landing.pricingLabel') || 'Simple pricing'}
              </p>
              <h2 className="font-['Playfair_Display'] text-4xl md:text-5xl font-medium text-[#1c1917] mb-5 leading-tight">
                {t('v2.pricingTitle') || 'Start free. Always honest.'}
              </h2>
              <p className="text-lg text-[#57534e] max-w-2xl mx-auto leading-relaxed">
                {t('v2.pricingDesc') || 'Your first few conversations are free. When you want more, a small top-up keeps going for weeks. No subscriptions, no surprises.'}
              </p>
            </motion.div>

            <div className="grid md:grid-cols-3 gap-6 max-w-3xl mx-auto mb-10">
              {[
                { icon: Zap, name: t('landing.pricingStarter') || 'Starter', price: '$5', sessions: '~10', color: 'text-amber-500' },
                { icon: Star, name: t('landing.pricingStandard') || 'Standard', price: '$10', sessions: '~20', color: 'text-[#b45309]', featured: true },
                { icon: Crown, name: t('landing.pricingPremium') || 'Premium', price: '$25', sessions: '~50', color: 'text-amber-700' },
              ].map((pkg, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.1 }}
                  whileHover={reduce ? {} : { y: -4 }}
                  className={`bg-white rounded-3xl border p-7 text-center transition-all ${
                    pkg.featured
                      ? 'border-[#b45309]/40 shadow-[0_20px_50px_-20px_rgba(180,83,9,0.3)]'
                      : 'border-[#d6d3d1]/50'
                  }`}
                >
                  <pkg.icon className={`w-8 h-8 ${pkg.color} mx-auto mb-3`} />
                  <p className="font-medium text-[#1c1917] mb-1">{pkg.name}</p>
                  <p className="font-['Playfair_Display'] text-4xl font-medium text-[#1c1917] mb-1">{pkg.price}</p>
                  <p className="text-sm text-[#78716c]">{pkg.sessions} {t('landing.pricingSessions') || 'sessions'}</p>
                </motion.div>
              ))}
            </div>

            <div className="text-center space-y-4">
              <button
                onClick={scrollToAuth}
                className="inline-flex items-center gap-2 bg-[#b45309] hover:bg-[#92400e] text-white px-8 py-3.5 rounded-full font-medium text-base transition-colors shadow-[0_8px_20px_-8px_rgba(180,83,9,0.5)]"
              >
                {t('landing.pricingCta') || 'Start Free'} <ChevronRight className="w-4 h-4" />
              </button>
              <p className="text-sm text-[#78716c]">
                <Link to="/gift" className="inline-flex items-center gap-1.5 text-[#b45309] font-medium hover:underline">
                  <Gift className="w-4 h-4" />
                  {t('landing.giftLink') || 'Gift a Life Story to someone you love'}
                </Link>
              </p>
            </div>
          </div>
        </section>

        {/* ═══════════════════ FACILITIES ═══════════════════ */}
        <section className="py-20 md:py-28">
          <div className="max-w-6xl mx-auto px-6 md:px-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
            >
              <div className="relative overflow-hidden bg-gradient-to-br from-blue-50 via-indigo-50 to-white rounded-[32px] border border-blue-200/40 p-8 md:p-14">
                {/* Decorative */}
                <div className="absolute top-0 right-0 w-64 h-64 bg-blue-200/30 rounded-full blur-3xl" />
                <div className="absolute bottom-0 left-0 w-48 h-48 bg-indigo-200/30 rounded-full blur-3xl" />

                <div className="relative grid md:grid-cols-2 gap-10 md:gap-14 items-center">
                  <div>
                    <p className="text-sm uppercase tracking-[0.2em] font-semibold text-blue-700 mb-4">
                      {t('landing.facilityLabel') || 'For Senior Living Communities'}
                    </p>
                    <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl font-medium text-[#1c1917] mb-5 leading-tight">
                      {t('landing.facilityTitle') || 'A meaningful activity your residents will love'}
                    </h2>
                    <p className="text-[#44403c] leading-relaxed mb-6 text-base">
                      {t('landing.facilityDesc') || 'LegacyLens works beautifully in assisted living and memory care settings. Staff tap "Start Session," hand over a tablet, and the AI guides the conversation. Families receive weekly updates. No training needed.'}
                    </p>
                    <ul className="space-y-3 mb-6">
                      {[
                        t('landing.facilityBullet1') || 'Facility dashboard with engagement tracking',
                        t('landing.facilityBullet2') || 'Weekly family email digests',
                        t('landing.facilityBullet3') || 'Downloadable life story PDFs for families',
                        t('landing.facilityBullet4') || 'Privacy-first: staff see engagement, not transcripts',
                      ].map((b, i) => (
                        <li key={i} className="flex items-start gap-3 text-[#1c1917]">
                          <span className="w-5 h-5 rounded-full bg-blue-600/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                            <Check className="w-3 h-3 text-blue-700" />
                          </span>
                          <span className="text-base">{b}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <div className="bg-white rounded-3xl border border-[#d6d3d1]/40 p-8 text-center shadow-[0_20px_50px_-20px_rgba(37,99,235,0.2)]">
                      <Building className="w-12 h-12 text-blue-600 mx-auto mb-5" />
                      <p className="font-['Playfair_Display'] text-2xl font-medium text-[#1c1917] mb-3">
                        {t('landing.facilityPilot') || 'Free 30-day pilot'}
                      </p>
                      <p className="text-[#57534e] mb-6 leading-relaxed">
                        {t('landing.facilityPilotDesc') || '5 residents, no cost, no commitment. See the response firsthand.'}
                      </p>
                      <a
                        href="https://calendar.app.google/KZwbHcuXqfXF87od7"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-full font-medium text-base transition-colors"
                      >
                        <Calendar className="w-4 h-4" />
                        {t('landing.facilityContact') || 'Book a call'}
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* ═══════════════════ AUTH / CTA ═══════════════════ */}
        <section ref={authRef} className="py-20 md:py-28">
          <div className="max-w-5xl mx-auto px-6 md:px-8">
            <div className="grid md:grid-cols-2 gap-10 md:gap-16 items-center">
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                <p className="text-sm uppercase tracking-[0.2em] font-semibold text-[#b45309] mb-4">
                  {t('v2.ctaLabel') || 'Begin today'}
                </p>
                <h2 className="font-['Playfair_Display'] text-4xl md:text-5xl font-medium text-[#1c1917] mb-5 leading-tight">
                  {t('v2.finalCtaTitle') || 'The best time was yesterday. The next best is now.'}
                </h2>
                <p className="text-lg text-[#44403c] leading-relaxed mb-6">
                  {t('v2.finalCtaDesc') || 'It takes less than a minute to start. Your first three conversations are free, forever.'}
                </p>
                <ul className="space-y-2 text-[#57534e]">
                  {[
                    t('v2.bullet1') || 'No credit card required',
                    t('v2.bullet2') || 'Private and encrypted',
                    t('v2.bullet3') || '15 languages supported',
                  ].map((b, i) => (
                    <li key={i} className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-[#b45309]" /> {b}
                    </li>
                  ))}
                </ul>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="w-full"
              >
                <div className="glass-card rounded-[28px] p-8 md:p-10 shadow-[0_20px_60px_-20px_rgba(28,25,23,0.15)]">
                  <h3 className="font-['Playfair_Display'] text-2xl font-medium text-[#1c1917] mb-2">
                    {t('landing.signInTitle') || 'Welcome'}
                  </h3>
                  <p className="text-[#78716c] mb-6">
                    {t('landing.signInSubtitle') || 'Sign in to start preserving stories'}
                  </p>

                  {isLoading ? (
                    <div className="flex items-center justify-center py-10">
                      <div className="w-8 h-8 border-2 border-[#b45309] border-t-transparent rounded-full animate-spin" />
                    </div>
                  ) : (
                    <>
                      <div className="flex justify-center mb-5">
                        <GoogleLogin
                          onSuccess={handleGoogleSuccess}
                          onError={() => toast.error(t('landing.googleError'))}
                          theme="outline"
                          size="large"
                          width="300"
                          shape="pill"
                          text="continue_with"
                        />
                      </div>

                      <div className="flex items-center gap-3 my-5">
                        <div className="flex-1 h-px bg-[#d6d3d1]/50" />
                        <span className="text-xs text-[#a8a29e] uppercase tracking-wider">
                          {t('landing.orDivider') || 'or'}
                        </span>
                        <div className="flex-1 h-px bg-[#d6d3d1]/50" />
                      </div>

                      <form onSubmit={handleEmailAuth} className="space-y-3">
                        {authMode === 'register' && (
                          <div className="relative">
                            <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#a8a29e]" />
                            <input
                              type="text"
                              value={name}
                              onChange={(e) => setName(e.target.value)}
                              placeholder={t('landing.namePlaceholder') || 'Full name'}
                              className="w-full pl-10 pr-4 py-3 rounded-xl border border-[#d6d3d1] bg-white text-[#1c1917] text-sm placeholder:text-[#a8a29e] focus:outline-none focus:border-[#b45309] focus:ring-2 focus:ring-[#b45309]/20 transition-colors"
                            />
                          </div>
                        )}
                        <div className="relative">
                          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#a8a29e]" />
                          <input
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder={t('landing.emailPlaceholder') || 'Email address'}
                            required
                            className="w-full pl-10 pr-4 py-3 rounded-xl border border-[#d6d3d1] bg-white text-[#1c1917] text-sm placeholder:text-[#a8a29e] focus:outline-none focus:border-[#b45309] focus:ring-2 focus:ring-[#b45309]/20 transition-colors"
                          />
                        </div>
                        {authMode !== 'forgot' && (
                          <div className="relative">
                            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#a8a29e]" />
                            <input
                              type="password"
                              value={password}
                              onChange={(e) => setPassword(e.target.value)}
                              placeholder={t('landing.passwordPlaceholder') || 'Password'}
                              required
                              minLength={8}
                              className="w-full pl-10 pr-4 py-3 rounded-xl border border-[#d6d3d1] bg-white text-[#1c1917] text-sm placeholder:text-[#a8a29e] focus:outline-none focus:border-[#b45309] focus:ring-2 focus:ring-[#b45309]/20 transition-colors"
                            />
                          </div>
                        )}
                        <button
                          type="submit"
                          className="w-full py-3 rounded-xl bg-[#b45309] hover:bg-[#92400e] text-white text-sm font-medium transition-colors shadow-[0_4px_12px_-2px_rgba(180,83,9,0.4)]"
                        >
                          {authMode === 'forgot'
                            ? (t('landing.sendResetLink') || 'Send Reset Link')
                            : authMode === 'register'
                              ? (t('landing.registerBtn') || 'Create Account')
                              : (t('landing.signInBtn') || 'Sign In')}
                        </button>
                      </form>

                      <div className="mt-4 text-center space-y-2">
                        {authMode === 'login' && (
                          <button
                            onClick={() => setAuthMode('forgot')}
                            className="text-xs text-[#78716c] hover:text-[#b45309] transition-colors"
                          >
                            {t('landing.forgotPassword') || 'Forgot password?'}
                          </button>
                        )}
                        {authMode === 'forgot' ? (
                          <p className="text-sm text-[#78716c]">
                            <button
                              onClick={() => setAuthMode('login')}
                              className="text-[#b45309] font-medium hover:underline"
                            >
                              {t('landing.backToSignIn') || 'Back to sign in'}
                            </button>
                          </p>
                        ) : (
                          <p className="text-sm text-[#78716c]">
                            {authMode === 'login'
                              ? (t('landing.switchToRegister') || "Don't have an account? ")
                              : (t('landing.switchToSignIn') || 'Already have an account? ')}
                            <button
                              onClick={() => setAuthMode(authMode === 'login' ? 'register' : 'login')}
                              className="text-[#b45309] font-medium hover:underline"
                            >
                              {authMode === 'login'
                                ? (t('landing.registerLink') || 'Create one')
                                : (t('landing.signInLink') || 'Sign in')}
                            </button>
                          </p>
                        )}
                      </div>
                    </>
                  )}

                  <div className="mt-6 pt-6 border-t border-[#d6d3d1]/30 text-center">
                    <a href="/replica" className="text-[#b45309] text-sm font-medium hover:underline">
                      {t('landing.replicaLink') || 'Looking for a loved one\'s replica? Access here'}
                    </a>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* ═══════════════════ FOOTER ═══════════════════ */}
        <footer className="border-t border-[#d6d3d1]/30 py-10 mt-10">
          <div className="max-w-7xl mx-auto px-6 md:px-8 flex flex-col md:flex-row items-center justify-between gap-4 text-sm text-[#78716c]">
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 rounded-full bg-[#b45309] flex items-center justify-center">
                <BookOpen className="w-3 h-3 text-white" />
              </div>
              <span className="font-['Playfair_Display'] font-medium text-[#1c1917]">LegacyLens</span>
              <span className="ml-2">&copy; {new Date().getFullYear()}</span>
            </div>
            <div className="flex items-center gap-6 flex-wrap justify-center">
              <Link to="/terms" className="hover:text-[#b45309] transition-colors">
                {t('landing.termsLink') || 'Terms'}
              </Link>
              <a href="/replica" className="hover:text-[#b45309] transition-colors">
                {t('landing.descendantPortal') || 'Descendant Portal'}
              </a>
              <Link to="/classic" className="hover:text-[#b45309] transition-colors">
                {t('v2.classicView') || 'Classic view'}
              </Link>
              {(() => {
                const [route, guide] = GUIDES.find(([, p]) => p.lang === language) || GUIDES.find(([, p]) => p.lang === 'en');
                return (
                  <Link to={route} className="hover:text-[#b45309] transition-colors">
                    {guide.shortTitle}
                  </Link>
                );
              })()}
              <Link to="/senior-living" className="hover:text-[#b45309] transition-colors">
                For senior living
              </Link>
            </div>
          </div>
          <nav className="max-w-7xl mx-auto px-6 md:px-8 pt-6 flex flex-wrap justify-center gap-x-5 gap-y-2 text-sm text-[#78716c]">
            {seoLanguages.languages.map((l) => (
              <Link
                key={l.code}
                to={l.path}
                hrefLang={l.hreflang}
                lang={l.code}
                onClick={() => setLanguage(l.code)}
                className={`hover:text-[#b45309] transition-colors ${language === l.code ? 'text-[#b45309] font-medium' : ''}`}
              >
                {l.label}
              </Link>
            ))}
          </nav>
          <BuiltBy lang={language} className="max-w-7xl mx-auto px-6 md:px-8 pt-6 text-center text-sm text-[#78716c]" />
        </footer>
      </div>
    </div>
  );
}
